import { API } from 'aws-amplify';
import { t } from 'i18next';
import { useContext, useState, useEffect, cloneElement } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import calcSchoolYear from '../../utils/calcSchoolYear';
import { ProtectedRouteContext } from '../Context';
import Button from '../shared/Button';
import Grid from '../shared/Grid';
import View from '../shared/View';

import './Renewals.scss';
import { Box, Dialog, DialogContent, DialogTitle } from '@mui/material';

const renewalsHeaders = [
    {
        colLabel: t('Students.AcceptRenewals.StudentNameHeader'),
        colName: 'StudentName',
        colSize: '10%',
    },
    {
        colLabel: t('Students.AcceptRenewals.AcceptRenewalHeader'),
        colName: 'checkbox',
    },
];

export default function Renewals(props) {
    const { userSettings, displayModal } = useContext(ProtectedRouteContext);
    const history = useHistory();
    const [doingAjax, setDoingAjax] = useState(true);
    const [initialized, setInitialized] = useState(false);
    const [renewals, setRenewals] = useState(null);
    const [selectedStudents, setSelectedStudents] = useState(() => {
        return new Set();
    });
    const [schoolYear] = useState(calcSchoolYear());
    const [abbrvSchoolYear] = useState((schoolYear.slice(2, 7) + schoolYear.slice(9)).replace(/ /g, ''));
    const [schoolName, setSchoolName] = useState(
        typeof userSettings?.SchoolName === 'string' ? userSettings.SchoolName : 'School Name Here'
    );
    const [ajaxErrorMessage] = useState(t('General.AjaxError'));

    const renewalsTitleJSX = (
        <h2>
            <span>{t('Students.AcceptRenewals.TitleAccept') + ' '}</span>
            <span className="renewals-title-blue">{t('Students.AcceptRenewals.TitleRenewals')}</span>
        </h2>
    );

    const renewalGridTitleJSX = (
        <div>
            <div className="grid-title">
                {t('Students.AcceptRenewals.GridTitleSchoolYearAbbreviation') +
                    ' ' +
                    abbrvSchoolYear +
                    ' ' +
                    t('Students.AcceptRenewals.TitleRenewals')}
            </div>
            <div className="grid-description">{t('Students.AcceptRenewals.GridDescription')}</div>
        </div>
    );

    const displayAcceptance = (toggle) => {
        setDoingAjax(toggle);
        let param2 = toggle
            ? () => (
                  <Dialog open={true}>
                      <DialogTitle>{t('Students.AcceptRenewals.ModalTitle')}</DialogTitle>
                      <DialogContent className="accept-renewals-modal">
                          <Box>
                              <div className="accept-renewals-sentence intro-sentence">
                                  {t('Students.AcceptRenewals.OnBehalfOf') + ' ' + schoolName + ' ' + t('Students.AcceptRenewals.WishTo')}
                              </div>
                              <div className="accept-renewals-modal-boxed-sentence">
                                  <span className="green-text">{t('Students.AcceptRenewals.ModalAccept')}</span>
                                  {' ' +
                                      t('Students.AcceptRenewals.AcceptMessage1') +
                                      ' ' +
                                      schoolYear +
                                      ' ' +
                                      t('Students.AcceptRenewals.AcceptMessage2')}
                              </div>
                              <div className="accept-renewals-sentence description-sentence">
                                  {t('Students.AcceptRenewals.AcceptMessage3')}
                              </div>
                          </Box>
                          <Box>
                              <Button
                                  useMui={true}
                                  type="grey"
                                  subType="button"
                                  onClick={() => {
                                      displayAcceptance(false);
                                  }}
                              >
                                  {t('General.Cancel')}
                              </Button>
                              <Button
                                  useMui={true}
                                  interactive={false}
                                  onClick={handleConfirm}
                              >
                                  {t('General.Confirm')}
                              </Button>
                          </Box>
                      </DialogContent>
                  </Dialog>
              )
            : null;
        displayModal(toggle, param2);
    };

    const handleConfirm = async () => {
        try {
            await API.patch('ShipyardAPI', '/students/renewals', {
                queryStringParameters: {
                    selectedIds: Array.from(selectedStudents).join(','),
                },
            });

            toast.success(t('Students.Renewals.UpdateSuccess'));
            setInitialized(false);
            displayAcceptance(false);
        } catch (e) {
            toast.error(t('Students.Renewals.UpdateFailure'));
        }
    };

    const handleAccept = () => {
        if (selectedStudents.size > 0) {
            displayAcceptance(true);
        } else {
            toast.error('At least one student needs to be selected for acceptance.');
        }
    };

    useEffect(() => {
        if (!initialized && userSettings.Retrieved) {
            async function getRenewals() {
                try {
                    setDoingAjax(true);
                    let response = await API.get('ShipyardAPI', '/students/renewals', {
                        queryStringParameters: {
                            term: 8,
                        },
                    });

                    const handleClick = (event, student) => {
                        if (event?.target?.checked) {
                            setSelectedStudents((prev) => new Set(prev).add(student));
                        } else {
                            setSelectedStudents((prev) => {
                                let _newState = new Set(prev);
                                _newState.delete(student);
                                return _newState;
                            });
                        }
                    };

                    setDoingAjax(false);
                    let checkboxJSX = <input type="checkbox" />;

                    let _newRenewals = response.renewals.map((student) => {
                        let _checkboxJSX = cloneElement(checkboxJSX, {
                            key: 'renewal-' + student.AppInstID,
                            onClick: (event) => {
                                handleClick(event, student.AppInstID);
                            },
                        });

                        let _studentClick = () => {
                            history.push('/students/record/' + student.AppInstID + '?renewals');
                        };

                        _checkboxJSX = <span>{_checkboxJSX}</span>;
                        let _studentJSX = (
                            <button
                                className="button-link"
                                colname="studentName"
                                onClick={_studentClick}
                            >
                                {student.StudentName}
                            </button>
                        );

                        return { data: student, columns: [_studentJSX, _checkboxJSX] };
                    });

                    _newRenewals = Object.assign(
                        { rows: null },
                        {
                            rows: _newRenewals,
                        }
                    );

                    setRenewals(_newRenewals);

                    setSchoolName(typeof userSettings?.SchoolName === 'string' ? userSettings.SchoolName : 'Your School Here');

                    setInitialized(true);
                } catch (e) {
                    toast.error(ajaxErrorMessage);
                    setDoingAjax(false);
                }
            }
            getRenewals();
        }
    }, [initialized, userSettings, history, userSettings.Retrieved, ajaxErrorMessage]);

    return (
        <View
            viewTitle={renewalsTitleJSX}
            doingAjax={doingAjax}
            className="renewals-view"
            name="renewals"
        >
            {initialized && renewals?.rows?.length > 0 ? (
                <Button
                    className="renewals-button"
                    type="tertiary"
                    onClick={handleAccept}
                >
                    {t('Students.AcceptRenewals.AcceptRenewalsButton')}
                </Button>
            ) : null}
            {/* <Button
                className="renewals-button"
                type="tertiary"
                onClick={handleAccept}
                disabled={initialized && renewals?.rows?.length === 0}
            >
                {t("Students.AcceptRenewals.AcceptRenewalsButton")}
            </Button> */}
            {renewals?.rows?.length > 0 ? (
                <Grid
                    name="renewals-grid"
                    className="renewals-grid"
                    title={renewalGridTitleJSX}
                    headers={renewalsHeaders}
                    data={renewals}
                    pageSize={5}
                    filters={false}
                />
            ) : initialized ? (
                <div className="no-renewals">No pending renewals to display.</div>
            ) : null}
            <Button
                className="back-button"
                type="primary"
                onClick={() => {
                    history.push('/students');
                }}
            >
                {t('Students.AcceptRenewals.BackButton')}
            </Button>
        </View>
    );
}
